import { ReactNode } from 'react';
import {
  Apex,
  ApexTrigger,
  Assign,
  At,
  Automations,
  Clock,
  Copy,
  CPQ,
  Cube,
  Fields,
  Flows,
  Layout,
  PlaybookAlert,
  RecordType,
  Reporting,
  Shuffle,
  Stamp,
  Target,
  ValidationRules,
  Wrench,
} from '@sweep-io/sweep-design/dist/icons';
import { colors } from '@sweep-io/sweep-design';
import { ObjectWithPills } from '../types';
import { ConfigurationItem } from '../../parser/ParserTypes';
import { ConfigurationType } from '@server/parser';

export { ConfigurationType };

export enum DependenciesContentViewType {
  default = 'default',
  comments = 'comments',
  sourceCode = 'sourceCode',
  editTag = 'editTag',
}

export const SWEEP_AUTOMATION_TEMPLATE_TYPES = [
  ConfigurationType.sweepAutomation,
  ConfigurationType.sweepAlert,
  ConfigurationType.sweepAssignment,
  ConfigurationType.sweepScheduledAssignment,
];

export const SWEEP_DEDUP_MATCHING_TEMPLATE_TYPES = [
  ConfigurationType.sweepDedupe,
  ConfigurationType.sweepMatching,
];

export const ConfigurationToName: {
  [key: string]: string;
} = {
  [ConfigurationType.validationRules]: 'Validation Rule',
  [ConfigurationType.workflowRules]: 'Workflow Rule',
  [ConfigurationType.flows]: 'Flow',
  [ConfigurationType.processBuilderFlows]: 'Process Builder',
  [ConfigurationType.approvalProcesses]: 'Approval Process',
  [ConfigurationType.apexTriggers]: 'Apex Trigger',
  [ConfigurationType.apexClasses]: 'Apex Class',
  [ConfigurationType.reports]: 'Report',
  [ConfigurationType.layouts]: 'Layout',
  [ConfigurationType.fields]: 'Field',
  [ConfigurationType.objects]: 'Object',
  [ConfigurationType.emailTemplates]: 'Email Template',
  [ConfigurationType.sweepAutomation]: 'Sweep Automation',
  [ConfigurationType.sweepAlert]: 'Sweep Alert',
  [ConfigurationType.sweepDedupe]: 'Sweep Deduplication',
  [ConfigurationType.sweepMatching]: 'Sweep Matching',
  [ConfigurationType.sweepAssignment]: 'Sweep Assignment',
  [ConfigurationType.sweepScheduledAssignment]: 'Sweep Scheduled Assignment',
  [ConfigurationType.rollups]: 'Rollup',
  [ConfigurationType.cpqData]: 'CPQ Configuration',
  [ConfigurationType.recordTypes]: 'Record Type',
  [ConfigurationType.relatedObjects]: 'Related Object',
  [ConfigurationType.others]: 'Other',
  standardFields: 'Standard Fields',
  customFields: 'Custom Fields',
};
const iconColor = colors.grey[700];

export const configurationTypeToIcon: {
  [key: string]: ReactNode;
} = {
  [ConfigurationType.fields]: <Fields color={iconColor} size="medium" />,
  [ConfigurationType.apexTriggers]: <ApexTrigger color={iconColor} size="medium" />,
  [ConfigurationType.apexClasses]: <Apex color={iconColor} size="medium" />,
  [ConfigurationType.validationRules]: <ValidationRules color={iconColor} size="medium" />,
  [ConfigurationType.workflowRules]: <Shuffle color={iconColor} size="medium" />,
  [ConfigurationType.flows]: <Flows color={iconColor} size="medium" />,
  [ConfigurationType.processBuilderFlows]: <Wrench color={iconColor} size="medium" />,
  [ConfigurationType.approvalProcesses]: <Stamp color={iconColor} size="medium" />,
  [ConfigurationType.reports]: <Reporting color={iconColor} size="medium" />,
  [ConfigurationType.layouts]: <Layout color={iconColor} size="medium" />,
  [ConfigurationType.objects]: <Cube color={iconColor} size="medium" />,
  [ConfigurationType.emailTemplates]: <At color={iconColor} size="medium" />,
  [ConfigurationType.sweepAutomation]: <Automations color={iconColor} size="medium" />,
  [ConfigurationType.sweepAlert]: <PlaybookAlert color={iconColor} size="medium" />,
  [ConfigurationType.sweepDedupe]: <Copy color={iconColor} size="medium" />,
  [ConfigurationType.sweepMatching]: <Target color={iconColor} size="medium" />,
  [ConfigurationType.sweepAssignment]: <Assign color={iconColor} size="medium" />,
  [ConfigurationType.sweepScheduledAssignment]: <Clock color={iconColor} size="medium" />,
  [ConfigurationType.rollups]: <Fields color={iconColor} size="medium" />,
  [ConfigurationType.cpqData]: <CPQ color={iconColor} size="medium" />,
  [ConfigurationType.recordTypes]: <RecordType color={iconColor} size="medium" />,
  [ConfigurationType.relatedObjects]: <></>,
  standardFields: <Fields color={iconColor} size="medium" />,
  customFields: <Fields color={iconColor} size="medium" />,
};

export type SelectedDependencyWithClearHistoryProps = SelectedDependency & {
  clearHistory?: boolean;
};

export type ConfigurationTypesWithIcon =
  | ConfigurationType.sweepAlert
  | ConfigurationType.sweepAutomation
  | ConfigurationType.sweepAssignment
  | ConfigurationType.sweepScheduledAssignment
  | ConfigurationType.sweepDedupe
  | ConfigurationType.sweepMatching;

export enum UnclassifiedTypes {
  tag = 'tag',
}

export interface SelectedDependency {
  id: string;
  name: string;
  dependencyType?: string;
  parentType: string;
  objectName?: string;
  contentType: DependenciesContentViewType;
  metadataId?: string;
  label?: string;
}

export interface ObjectWithPillsMap {
  favorites: ObjectWithPills[];
  standardObjects: ObjectWithPills[];
  cpqObjects: ObjectWithPills[];
  customObjects: ObjectWithPills[];
  platformEvents: ObjectWithPills[];
}

export interface LayoutsByObjectName {
  [objectName: string]: { isLoading: boolean; layouts: ConfigurationItem[] };
}
